/**
 * Sandbox Persona Manager Component
 *
 * Full CRUD interface for managing sandbox personas.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  ContentCopy as CloneIcon,
  Person as PersonIcon,
  Psychology as TherapistIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import * as researcherApi from '../../services/researcherApi';

const SandboxPersonaManager = () => {
  const [personas, setPersonas] = useState({ owned: [], shared: [] });
  const [livePersonas, setLivePersonas] = useState({ client: [], therapist: [] });
  const [researchers, setResearchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [formData, setFormData] = useState({
    personaType: 'client',
    key: '',
    name: '',
    system_prompt: '',
    description: '',
    opening_line: '',
    modalities: [],
    metadata: { conditions: [], severity: null }
  });
  const [shareData, setShareData] = useState({ targetUserId: '', permission: 'view' });
  const [cloneData, setCloneData] = useState({ personaType: 'client', livePersonaKey: '', newKey: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [personasData, clientPersonas, therapistPersonas, researchersData] = await Promise.all([
        researcherApi.listSandboxPersonas(),
        researcherApi.getLiveClientPersonas(),
        researcherApi.getLiveTherapistPersonas(),
        researcherApi.getOtherResearchers()
      ]);
      setPersonas(personasData);
      setLivePersonas({
        client: clientPersonas.personas || [],
        therapist: therapistPersonas.personas || []
      });
      setResearchers(researchersData);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (persona = null) => {
    if (persona) {
      setSelectedPersona(persona);
      setFormData({
        personaType: persona.personaType,
        key: persona.key,
        name: persona.name,
        system_prompt: persona.system_prompt || '',
        description: persona.description || '',
        opening_line: persona.opening_line || '',
        modalities: persona.modalities || [],
        metadata: persona.metadata || { conditions: [], severity: null }
      });
    } else {
      setSelectedPersona(null);
      setFormData({
        personaType: 'client',
        key: '',
        name: '',
        system_prompt: '',
        description: '',
        opening_line: '',
        modalities: [],
        metadata: { conditions: [], severity: null }
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (selectedPersona) {
        await researcherApi.updateSandboxPersona(selectedPersona._id, formData);
      } else {
        await researcherApi.createSandboxPersona(formData);
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to save persona');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (personaId) => {
    if (!window.confirm('Are you sure you want to delete this persona?')) return;
    try {
      await researcherApi.deleteSandboxPersona(personaId);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to delete persona');
    }
  };

  const handleShare = async () => {
    try {
      setSaving(true);
      await researcherApi.sharePersona(selectedPersona._id, shareData.targetUserId, shareData.permission);
      setShareDialogOpen(false);
      setShareData({ targetUserId: '', permission: 'view' });
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to share persona');
    } finally {
      setSaving(false);
    }
  };

  const handleUnshare = async (personaId, userId) => {
    try {
      await researcherApi.unsharePersona(personaId, userId);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to unshare persona');
    }
  };

  const handleClone = async () => {
    try {
      setSaving(true);
      await researcherApi.cloneFromLive(
        cloneData.personaType,
        cloneData.livePersonaKey,
        cloneData.newKey || null
      );
      setCloneDialogOpen(false);
      setCloneData({ personaType: 'client', livePersonaKey: '', newKey: '' });
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to clone persona');
    } finally {
      setSaving(false);
    }
  };

  const openShareDialog = (persona) => {
    setSelectedPersona(persona);
    setShareDialogOpen(true);
  };

  const renderPersonaCard = (persona, isOwned = true) => (
    <Grid item xs={12} md={6} lg={4} key={persona._id}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Avatar sx={{ bgcolor: persona.personaType === 'client' ? 'info.light' : 'success.light' }}>
              {persona.personaType === 'client' ? <PersonIcon /> : <TherapistIcon />}
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="medium">
                {persona.name}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Key: {persona.key}
              </Typography>
              <Box display="flex" gap={0.5} mt={1}>
                <Chip label={persona.personaType} size="small" variant="outlined" />
                <Chip label={`v${persona.version || 1}`} size="small" variant="outlined" />
              </Box>
              {persona.description && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                >
                  {persona.description}
                </Typography>
              )}
              {persona.sharedWith?.length > 0 && isOwned && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  Shared with {persona.sharedWith.length} researcher(s)
                </Typography>
              )}
              {!isOwned && persona.createdBy && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  Owner: {persona.createdBy.name || persona.createdBy.email}
                </Typography>
              )}
            </Box>
          </Box>
          {isOwned && (
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Tooltip title="Share">
                <IconButton size="small" onClick={() => openShareDialog(persona)}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleOpenDialog(persona)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(persona._id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Action Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Create Persona
        </Button>
        <Button variant="outlined" startIcon={<CloneIcon />} onClick={() => setCloneDialogOpen(true)}>
          Clone from Live
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`My Personas (${personas.owned?.length || 0})`} />
        <Tab label={`Shared with Me (${personas.shared?.length || 0})`} />
      </Tabs>

      {/* Personas Grid */}
      {tabValue === 0 && (
        <Grid container spacing={2}>
          {personas.owned?.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                You haven't created any sandbox personas yet. Click "Create Persona" or "Clone from Live" to get started.
              </Alert>
            </Grid>
          ) : (
            personas.owned?.map(p => renderPersonaCard(p, true))
          )}
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={2}>
          {personas.shared?.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No personas have been shared with you yet.
              </Alert>
            </Grid>
          ) : (
            personas.shared?.map(p => renderPersonaCard(p, false))
          )}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPersona ? 'Edit Persona' : 'Create Persona'}
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Persona Type</InputLabel>
                <Select
                  value={formData.personaType}
                  label="Persona Type"
                  onChange={(e) => setFormData({ ...formData, personaType: e.target.value })}
                  disabled={!!selectedPersona}
                >
                  <MenuItem value="client">Client</MenuItem>
                  <MenuItem value="therapist">Therapist</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key"
                margin="normal"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                disabled={!!selectedPersona}
                helperText="Unique identifier (no spaces)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                margin="normal"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                margin="normal"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="System Prompt"
                margin="normal"
                multiline
                rows={6}
                value={formData.system_prompt}
                onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                helperText="The AI instructions for this persona"
              />
            </Grid>
            {formData.personaType === 'client' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opening Line"
                  margin="normal"
                  multiline
                  rows={2}
                  value={formData.opening_line}
                  onChange={(e) => setFormData({ ...formData, opening_line: e.target.value })}
                  helperText="First message the client sends"
                />
              </Grid>
            )}
            {formData.personaType === 'therapist' && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Modalities</InputLabel>
                  <Select
                    multiple
                    value={formData.modalities}
                    label="Modalities"
                    onChange={(e) => setFormData({ ...formData, modalities: e.target.value })}
                    renderValue={(selected) => (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                      </Box>
                    )}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="voice">Voice</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={['anxiety', 'depression', 'PTSD', 'OCD', 'ADHD', 'relationship', 'stress', 'grief']}
                value={formData.metadata?.conditions || []}
                onChange={(e, newValue) => setFormData({
                  ...formData,
                  metadata: { ...formData.metadata, conditions: newValue }
                })}
                renderInput={(params) => (
                  <TextField {...params} label="Conditions/Tags" margin="normal" />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (selectedPersona ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Persona</DialogTitle>
        <DialogContent dividers>
          {selectedPersona && (
            <Typography variant="body2" gutterBottom>
              Sharing: <strong>{selectedPersona.name}</strong>
            </Typography>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Researcher</InputLabel>
            <Select
              value={shareData.targetUserId}
              label="Researcher"
              onChange={(e) => setShareData({ ...shareData, targetUserId: e.target.value })}
            >
              {researchers.map(r => (
                <MenuItem key={r._id} value={r._id}>
                  {r.name} ({r.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Permission</InputLabel>
            <Select
              value={shareData.permission}
              label="Permission"
              onChange={(e) => setShareData({ ...shareData, permission: e.target.value })}
            >
              <MenuItem value="view">View (can view and use in simulations)</MenuItem>
              <MenuItem value="edit">Edit (can also edit persona)</MenuItem>
              <MenuItem value="full">Full (can also delete)</MenuItem>
            </Select>
          </FormControl>
          {selectedPersona?.sharedWith?.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Currently shared with:</Typography>
              <List dense>
                {selectedPersona.sharedWith.map((share, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={share.userId?.name || share.userId?.email || 'Unknown'}
                      secondary={`Permission: ${share.permission}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleUnshare(selectedPersona._id, share.userId?._id || share.userId)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={saving || !shareData.targetUserId}
          >
            {saving ? <CircularProgress size={20} /> : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone from Live Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone from Live Persona</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Create a sandbox copy of a live persona for experimentation.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Persona Type</InputLabel>
            <Select
              value={cloneData.personaType}
              label="Persona Type"
              onChange={(e) => setCloneData({ ...cloneData, personaType: e.target.value, livePersonaKey: '' })}
            >
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="therapist">Therapist</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Live Persona</InputLabel>
            <Select
              value={cloneData.livePersonaKey}
              label="Live Persona"
              onChange={(e) => setCloneData({ ...cloneData, livePersonaKey: e.target.value })}
            >
              {(cloneData.personaType === 'client' ? livePersonas.client : livePersonas.therapist).map(p => (
                <MenuItem key={p.key} value={p.key}>
                  {p.name} ({p.key})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="New Key (optional)"
            margin="normal"
            value={cloneData.newKey}
            onChange={(e) => setCloneData({ ...cloneData, newKey: e.target.value })}
            helperText="Leave empty to auto-generate"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleClone}
            disabled={saving || !cloneData.livePersonaKey}
          >
            {saving ? <CircularProgress size={20} /> : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SandboxPersonaManager;
